
// ---------- shared helpers ----------
function waitForStorageKeys(keys, { maxWaitTime = 3000, pollInterval = 50 } = {}) {
  return new Promise((resolve) => {
    const start = Date.now();

    const check = () => {
      const values = keys.map((k) => localStorage.getItem(k));
      const allAvailable = values.every((v) => v !== null);

      if (allAvailable || Date.now() - start >= maxWaitTime) {
        resolve(Object.fromEntries(keys.map((k, i) => [k, values[i]])));
        return;
      }

      setTimeout(check, pollInterval);
    };

    check();
  });
}

function waitForFbqReady({ maxWaitTime = 3000, pollInterval = 50 } = {}) {
  return new Promise((resolve) => {
    const start = Date.now();

    const check = () => {
      const ready = typeof fbq !== "undefined";
      if (ready || Date.now() - start >= maxWaitTime) {
        resolve(ready);
        return;
      }
      setTimeout(check, pollInterval);
    };

    check();
  });
}

function waitForGtagReady({ maxWaitTime = 3000, pollInterval = 50 } = {}) {
  return new Promise((resolve) => {
    const start = Date.now();

    const check = () => {
      const ready = typeof gtag !== "undefined";
      if (ready || Date.now() - start >= maxWaitTime) {
        resolve(ready);
        return;
      }
      setTimeout(check, pollInterval);
    };

    check();
  });
}

function getCurrencyCode() {
  let currencyCode = "SGD";
  if (typeof Shopify !== "undefined" && Shopify.currency && Shopify.currency.active) {
    currencyCode = Shopify.currency.active;
  }
  return currencyCode;
}

function makeEventId(eventName) {
  return `${eventName}_${Date.now()}`;
}

// ---------- 1) META (pixel + CAPI) ----------
function trackMeta(eventName, customData = {}) {
  if (localStorage.getItem('devMode_noTracking') === 'true') {
    console.log('[devMode] trackMeta blocked:', eventName, customData);
    return;
  }
  console.log('trackMeta', eventName, customData);
  const maxWaitTime = 3000;
  const pollInterval = 50;

  (async () => {
    const storage = await waitForStorageKeys(["emailMailchimp", "gender"], {
      maxWaitTime,
      pollInterval,
    });

    const fbqReady = await waitForFbqReady({ maxWaitTime, pollInterval });

    // fbc = formatted _fbc cookie value (fb.1.{ms}.{fbclid}) — correct for CAPI user_data.fbc.
    // fbp = _fbp cookie set by Meta Pixel — identity signal sent alongside fbc.
    // Do NOT send raw fbclid here; the backend must use fbc (formatted) for user_data.fbc.
    const fbp = sessionStorage.getItem("fbp");
    const fbc = sessionStorage.getItem("fbc");
    const external_id = sessionStorage.getItem("external_id");
    const referrer_url = sessionStorage.getItem("referrer_url");
    const utm_source = sessionStorage.getItem("utm_source");
    const utm_medium = sessionStorage.getItem("utm_medium");
    const utm_campaign = sessionStorage.getItem("utm_campaign");
    const utm_term = sessionStorage.getItem("utm_term");
    const utm_content = sessionStorage.getItem("utm_content");
    const utm_id = sessionStorage.getItem("utm_id");

    const eventId = makeEventId(eventName);
    const currencyCode = getCurrencyCode();

    if (fbqReady) {
      fbq("trackCustom", eventName, customData, { eventID: eventId });
    }

    fetch("https://us-central1-nutricodeapp-3da5f.cloudfunctions.net/sendToMeta", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: location.href,
        referrer_url,
        action_source: "website",
        client_user_agent: navigator.userAgent,
        client_ip_address: null,
        email: storage.emailMailchimp,
        gender: storage.gender,
        fbp,
        fbc,
        external_id,
        page_title: document.title,
        currency: currencyCode,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        utm_id,
        ...customData,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Meta CAPI failed");
        return res.json();
      })
      .then((data) => {
        console.log(`✅ Meta CAPI "${eventName}" sent:`, data);
      })
      .catch((err) => {
        console.error(`❌ Meta CAPI "${eventName}" error:`, err);
      });
  })();
}

// ---------- 2) GOOGLE (gtag) ----------
function trackGoogle(eventName, params = {}) {
  if (localStorage.getItem('devMode_noTracking') === 'true') {
    console.log('[devMode] trackGoogle blocked:', eventName, params);
    return;
  }
  const maxWaitTime = 3000;
  const pollInterval = 50;

  (async () => {
    const gtagReady = await waitForGtagReady({ maxWaitTime, pollInterval });
    if (!gtagReady) return;

    gtag("event", eventName, { ...params });
  })();
}

// ---------- 3) META PIXEL ONLY (custom event name only) ----------
function trackMetaPixel(eventName) {
  if (localStorage.getItem('devMode_noTracking') === 'true') {
    console.log('[devMode] trackMetaPixel blocked:', eventName);
    return;
  }
  const maxWaitTime = 3000;
  const pollInterval = 50;

  (async () => {
    const fbqReady = await waitForFbqReady({ maxWaitTime, pollInterval });
    if (!fbqReady) return;

    const eventId = makeEventId(eventName);
    fbq("trackCustom", eventName, {}, { eventID: eventId });
  })();
}
